import React from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useCart, CartItem } from "../context/CartContext";
import EmptyState from "../components/ui/EmptyState";

const PageTitle = styled.h1`
  font-size: 1.75rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text};
  letter-spacing: -0.02em;
  margin-bottom: 2rem;
`;

const CartLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: 1fr 360px;
    align-items: flex-start;
  }
`;

const ItemsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const CartItemCard = styled.div`
  display: flex;
  gap: 1.25rem;
  padding: 1.25rem;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radii.lg};
  transition: border-color 0.2s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.borderHover};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
`;

const ItemImage = styled.img`
  width: 100px;
  height: 100px;
  object-fit: contain;
  flex-shrink: 0;
  border-radius: ${({ theme }) => theme.radii.md};
  background: #fafafa;
  padding: 0.5rem;
`;

const ItemInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  min-width: 0;
`;

const ItemTitle = styled.h3`
  font-size: 0.95rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ItemCategory = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  text-transform: capitalize;
`;

const ItemPrice = styled.span`
  font-size: 1.1rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
`;

const ItemActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: auto;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    justify-content: center;
  }
`;

const QuantityControl = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radii.md};
  overflow: hidden;
`;

const QtyButton = styled.button`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text};
  font-size: 1.1rem;
  font-weight: 600;
  transition: background 0.15s;

  &:hover {
    background: ${({ theme }) => theme.colors.border};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const QtyValue = styled.span`
  min-width: 40px;
  text-align: center;
  font-size: 0.9rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  background: ${({ theme }) => theme.colors.surface};
  padding: 0.35rem 0;
`;

const RemoveButton = styled.button`
  padding: 0.4rem 0.8rem;
  background: transparent;
  color: ${({ theme }) => theme.colors.error};
  font-size: 0.8rem;
  font-weight: 500;
  border-radius: ${({ theme }) => theme.radii.md};
  transition: all 0.15s;

  &:hover {
    background: ${({ theme }) => theme.colors.errorLight};
  }
`;

const Summary = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radii.xl};
  padding: 1.75rem;
  position: sticky;
  top: 80px;
`;

const SummaryTitle = styled.h2`
  font-size: 1.15rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 1.25rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const SummaryRow = styled.div<{ $bold?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  font-size: ${({ $bold }) => ($bold ? "1.1rem" : "0.9rem")};
  font-weight: ${({ $bold }) => ($bold ? "700" : "400")};
  color: ${({ $bold, theme }) =>
    $bold ? theme.colors.text : theme.colors.textSecondary};
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  margin: 0.75rem 0;
`;

const CheckoutButton = styled.button`
  width: 100%;
  padding: 0.85rem;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  font-weight: 600;
  font-size: 0.95rem;
  border-radius: ${({ theme }) => theme.radii.md};
  margin-top: 1.25rem;
  transition: background 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.primaryHover};
  }
`;

const ContinueLink = styled.button`
  width: 100%;
  padding: 0.7rem;
  background: transparent;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 500;
  font-size: 0.9rem;
  border-radius: ${({ theme }) => theme.radii.md};
  margin-top: 0.5rem;
  transition: background 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.primaryLight};
  }
`;

const ClearButton = styled.button`
  padding: 0.5rem 1rem;
  background: transparent;
  color: ${({ theme }) => theme.colors.error};
  font-size: 0.85rem;
  font-weight: 500;
  border-radius: ${({ theme }) => theme.radii.md};
  transition: all 0.15s;

  &:hover {
    background: ${({ theme }) => theme.colors.errorLight};
  }
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    gap: 0.75rem;
    align-items: flex-start;
  }
`;

const CartPage: React.FC = () => {
  const navigate = useNavigate();
  const { state, removeFromCart, updateQuantity, clearCart } = useCart();

  if (state.items.length === 0) {
    return (
      <>
        <PageTitle>Shopping Cart</PageTitle>
        <EmptyState message="Your cart is empty. Start shopping!" icon="🛒" />
        <div style={{ textAlign: "center" }}>
          <ContinueLink onClick={() => navigate("/")}>
            Browse Products →
          </ContinueLink>
        </div>
      </>
    );
  }

  return (
    <>
      <HeaderRow>
        <PageTitle style={{ marginBottom: 0 }}>
          Shopping Cart ({state.totalItems} item
          {state.totalItems !== 1 ? "s" : ""})
        </PageTitle>
        <ClearButton onClick={clearCart}>Clear Cart</ClearButton>
      </HeaderRow>

      <CartLayout>
        <ItemsList>
          {state.items.map((item: CartItem) => (
            <CartItemCard key={item.product.id}>
              <ItemImage src={item.product.image} alt={item.product.title} />
              <ItemInfo>
                <ItemCategory>{item.product.category}</ItemCategory>
                <ItemTitle>{item.product.title}</ItemTitle>
                <ItemPrice>
                  ${(item.product.price * item.quantity).toFixed(2)}
                </ItemPrice>
                <ItemActions>
                  <QuantityControl>
                    <QtyButton
                      onClick={() =>
                        updateQuantity(item.product.id, item.quantity - 1)
                      }
                    >
                      −
                    </QtyButton>
                    <QtyValue>{item.quantity}</QtyValue>
                    <QtyButton
                      onClick={() =>
                        updateQuantity(item.product.id, item.quantity + 1)
                      }
                    >
                      +
                    </QtyButton>
                  </QuantityControl>
                  <RemoveButton onClick={() => removeFromCart(item.product.id)}>
                    Remove
                  </RemoveButton>
                </ItemActions>
              </ItemInfo>
            </CartItemCard>
          ))}
        </ItemsList>

        <Summary>
          <SummaryTitle>Order Summary</SummaryTitle>
          <SummaryRow>
            <span>Subtotal ({state.totalItems} items)</span>
            <span>${state.totalPrice.toFixed(2)}</span>
          </SummaryRow>
          <SummaryRow>
            <span>Shipping</span>
            <span style={{ color: "#10B981", fontWeight: 600 }}>Free</span>
          </SummaryRow>
          <Divider />
          <SummaryRow $bold>
            <span>Total</span>
            <span>${state.totalPrice.toFixed(2)}</span>
          </SummaryRow>
          <CheckoutButton>Proceed to Checkout</CheckoutButton>
          <ContinueLink onClick={() => navigate("/")}>
            ← Continue Shopping
          </ContinueLink>
        </Summary>
      </CartLayout>
    </>
  );
};

export default CartPage;
